import { useState, useEffect, useMemo, Fragment } from "react";
import { useAuth } from "../contexts/AuthContext";
import { schedulesApi, usersApi, projectsApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addDays,
  addWeeks,
  addMonths,
  subWeeks,
  subMonths,
  isSameDay,
  isSameMonth,
  eachDayOfInterval,
  getDay,
  getISOWeek,
} from "date-fns";
import { sv } from "date-fns/locale";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  User,
  Trash2,
  X,
  MapPin,
  FileText,
  CalendarDays,
} from "lucide-react";
import { cn } from "../lib/utils";

const SCHEDULE_TYPES = [
  { value: "projekt", label: "Projekt", color: "#6dbf4a" },
  { value: "möte", label: "Möte", color: "#ec4899" },
  { value: "utbildning", label: "Utbildning", color: "#14b8a6" },
  { value: "resa", label: "Resa", color: "#f97316" },
  { value: "kontor", label: "Kontor", color: "#64748b" },
  { value: "hemma", label: "Hemma", color: "#22c55e" },
  { value: "semester", label: "Semester", color: "#3b82f6" },
  { value: "sjuk", label: "Sjuk", color: "#ef4444" },
  { value: "vab", label: "VAB", color: "#f59e0b" },
  { value: "annan ledighet", label: "Annan ledighet", color: "#8b5cf6" },
];

const Schedule = () => {
  const { isAdmin, user } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("month");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedUser, setSelectedUser] = useState("all");

  // Day detail popover
  const [dayDetailDate, setDayDetailDate] = useState(null);
  const [dayDetailOpen, setDayDetailOpen] = useState(false);

  const [form, setForm] = useState({
    user_ids: [],
    user_id: "",
    start_date: format(new Date(), "yyyy-MM-dd"),
    end_date: format(new Date(), "yyyy-MM-dd"),
    schedule_type: "projekt",
    custom_type: "",
    project_id: "",
    title: "",
    notes: "",
  });

  useEffect(() => {
    loadData();
  }, [currentDate, viewMode]);

  const loadData = async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      const [schedulesRes, projectsRes] = await Promise.all([
        schedulesApi.getAll({ start_date: start, end_date: end }),
        projectsApi.getAll(),
      ]);
      setSchedules(schedulesRes.data);
      setProjects(projectsRes.data.filter(p => p.is_active));
      
      // Users list only for admin
      if (isAdmin) {
        try {
          const usersRes = await usersApi.getAll();
          setUsers(usersRes.data);
        } catch (error) {
          // Not admin, skip
        }
      }
    } catch (error) {
      toast.error("Kunde inte ladda data");
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = () => {
    let start, end;
    switch (viewMode) {
      case "week":
        start = format(startOfWeek(currentDate, { weekStartsOn: 1 }), "yyyy-MM-dd");
        end = format(endOfWeek(currentDate, { weekStartsOn: 1 }), "yyyy-MM-dd");
        break;
      case "month":
      default:
        const monthStart = startOfMonth(currentDate);
        const monthEnd = endOfMonth(currentDate);
        start = format(startOfWeek(monthStart, { weekStartsOn: 1 }), "yyyy-MM-dd");
        end = format(endOfWeek(monthEnd, { weekStartsOn: 1 }), "yyyy-MM-dd");
        break;
    }
    return { start, end };
  };

  const navigatePrev = () => {
    if (viewMode === "week") setCurrentDate(subWeeks(currentDate, 1));
    else setCurrentDate(subMonths(currentDate, 1));
  };

  const navigateNext = () => {
    if (viewMode === "week") setCurrentDate(addWeeks(currentDate, 1));
    else setCurrentDate(addMonths(currentDate, 1));
  };

  const goToToday = () => setCurrentDate(new Date());

  const openAddDialog = (date = null, userId = null) => {
    const targetDate = date ? format(date, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd");
    setForm({
      user_ids: isAdmin ? (userId ? [userId] : []) : [user.id],
      user_id: isAdmin ? "" : user.id,
      start_date: targetDate,
      end_date: targetDate,
      schedule_type: "projekt",
      custom_type: "",
      project_id: "",
      title: "",
      notes: "",
    });
    setEditingSchedule(null);
    setDialogOpen(true);
  };

  const openEditDialog = (schedule) => {
    if (!isAdmin && schedule.user_id !== user.id) return;
    const knownType = SCHEDULE_TYPES.find(t => t.value === schedule.schedule_type);
    setForm({
      user_ids: [schedule.user_id],
      user_id: schedule.user_id,
      start_date: schedule.start_date,
      end_date: schedule.end_date,
      schedule_type: knownType ? schedule.schedule_type : "annat",
      custom_type: knownType ? "" : schedule.schedule_type,
      project_id: schedule.project_id || "",
      title: schedule.title || "",
      notes: schedule.notes || "",
    });
    setEditingSchedule(schedule);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const userIds = isAdmin ? form.user_ids : [user.id];
    if (userIds.length === 0) {
      toast.error("Välj minst en anställd");
      return;
    }
    setSubmitting(true);
    try {
      const baseData = { ...form };
      if (baseData.schedule_type === "annat" && baseData.custom_type.trim()) {
        baseData.schedule_type = baseData.custom_type.trim();
      }
      delete baseData.custom_type;
      delete baseData.user_ids;
      if (baseData.schedule_type !== "projekt") baseData.project_id = null;

      if (editingSchedule) {
        // Edit: update single entry
        baseData.user_id = userIds[0];
        await schedulesApi.update(editingSchedule.id, baseData);
        toast.success("Schema uppdaterat!");
      } else {
        // Create: one entry per selected user
        for (const uid of userIds) {
          await schedulesApi.create({ ...baseData, user_id: uid });
        }
        toast.success(`Schema skapat för ${userIds.length} ${userIds.length === 1 ? "person" : "personer"}!`);
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error("Kunde inte spara");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!confirm("Ta bort schemat?")) return;
    try {
      await schedulesApi.delete(scheduleId);
      toast.success("Schema borttaget");
      setDialogOpen(false);
      setDayDetailOpen(false);
      loadData();
    } catch (error) {
      toast.error("Kunde inte ta bort");
    }
  };

  const getSchedulesForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return schedules.filter((s) => {
      if (selectedUser !== "all" && s.user_id !== selectedUser) return false;
      return dateStr >= s.start_date && dateStr <= s.end_date;
    });
  };

  const getTypeInfo = (type) => {
    return SCHEDULE_TYPES.find((t) => t.value === type) || SCHEDULE_TYPES[0];
  };

  // Build user color map for consistent colors per user
  const userColorMap = useMemo(() => {
    const colors = ["#6dbf4a", "#3b82f6", "#ec4899", "#f59e0b", "#14b8a6", "#8b5cf6", "#ef4444", "#f97316"];
    const map = {};
    users.forEach((u, i) => { map[u.id] = colors[i % colors.length]; });
    return map;
  }, [users]);

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: calStart, end: calEnd });
  }, [currentDate]);

  const weekDays = ["Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön"];

  // Open day detail
  const openDayDetail = (day, e) => {
    e.stopPropagation();
    setDayDetailDate(day);
    setDayDetailOpen(true);
  };

  const dayDetailSchedules = dayDetailDate ? getSchedulesForDate(dayDetailDate) : [];

  if (loading && schedules.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6dbf4a]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-20" data-testid="schedule-page">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <CalendarDays className="w-5 h-5 text-[#6dbf4a]" />
              <h1 className="text-2xl font-bold text-white tracking-tight">Planering</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* View mode toggle */}
            <div className="flex bg-[#080c12] rounded-xl border border-white/[0.06] overflow-hidden">
              <button
                onClick={() => setViewMode("month")}
                className={cn("px-3 py-1.5 text-xs font-medium transition-colors",
                  viewMode === "month" ? "bg-[#6dbf4a] text-white" : "text-white/40 hover:text-white")}
                data-testid="view-month-btn"
              >Månad</button>
              <button
                onClick={() => setViewMode("week")}
                className={cn("px-3 py-1.5 text-xs font-medium transition-colors",
                  viewMode === "week" ? "bg-[#6dbf4a] text-white" : "text-white/40 hover:text-white")}
                data-testid="view-week-btn"
              >Vecka</button>
            </div>
            <Button onClick={() => openAddDialog()} size="sm" className="gap-1 bg-[#6dbf4a] hover:bg-[#5da83d]" data-testid="add-schedule-btn">
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Lägg till</span>
            </Button>
          </div>
        </div>

        {/* Navigation */}
        <Card className="bg-[#0d1218] border-white/[0.06]">
          <CardContent className="py-2 px-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={navigatePrev} className="h-8 w-8 text-white/40">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-white font-medium text-sm sm:text-base min-w-[140px] text-center">
                  {viewMode === "week"
                    ? `v${getISOWeek(currentDate)} · ${format(startOfWeek(currentDate, { weekStartsOn: 1 }), "d MMM", { locale: sv })} – ${format(endOfWeek(currentDate, { weekStartsOn: 1 }), "d MMM", { locale: sv })}`
                    : format(currentDate, "MMMM yyyy", { locale: sv })}
                </span>
                <Button variant="ghost" size="icon" onClick={navigateNext} className="h-8 w-8 text-white/40">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              <Button variant="outline" size="sm" onClick={goToToday} className="text-xs border-white/[0.06] text-white/40">
                Idag
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* User filter */}
        {isAdmin && users.length > 1 && (
          <Select value={selectedUser} onValueChange={setSelectedUser}>
            <SelectTrigger className="bg-[#0d1218] border-white/[0.06] text-white" data-testid="user-filter">
              <SelectValue placeholder="Alla anställda" />
            </SelectTrigger>
            <SelectContent className="bg-[#0d1218] border-white/[0.06]">
              <SelectItem value="all" className="text-white">Alla anställda</SelectItem>
              {users.map(u => (
                <SelectItem key={u.id} value={u.id} className="text-white">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: userColorMap[u.id] }} />
                    {u.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Legend */}
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap">
        {SCHEDULE_TYPES.map(type => (
          <div key={type.value} className="flex items-center gap-1.5 shrink-0">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: type.color }} />
            <span className="text-xs text-white/40 whitespace-nowrap">{type.label}</span>
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <Card className="bg-[#0d1218] border-white/[0.06] overflow-hidden relative">
        {/* Watermark logo */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0" style={{ opacity: 0.12 }}>
          <img src="https://customer-assets.emergentagent.com/job_bc07a5b0-4401-454c-9821-20d81dadd6da/artifacts/21c8lw1c_fa793a21-0229-4214-a16b-e1c0df8a86d3.png" alt="" className="w-[600px] h-auto" style={{ mixBlendMode: 'screen', filter: 'brightness(1.3)' }} />
        </div>

        {/* Week days header */}
        <div className="grid grid-cols-[2rem_1fr_1fr_1fr_1fr_1fr_1fr_1fr] border-b border-white/[0.06] relative z-10">
          <div className="py-3 text-center text-[10px] font-bold uppercase tracking-wider text-white/25">v</div>
          {weekDays.map((day, i) => (
            <div key={day} className={cn("py-3 text-center text-[11px] font-bold uppercase tracking-[0.15em]",
              i >= 5 ? "text-red-400/60 bg-red-500/[0.03]" : "text-white/25")}>
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="grid grid-cols-[2rem_1fr_1fr_1fr_1fr_1fr_1fr_1fr] relative z-10">
          {calendarDays.map((day, idx) => {
            const daySchedules = getSchedulesForDate(day);
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isToday = isSameDay(day, new Date());
            const isWeekend = getDay(day) === 0 || getDay(day) === 6;
            const hasMany = daySchedules.length > 2;
            const isMonday = getDay(day) === 1;

            return (
              <Fragment key={format(day, "yyyy-MM-dd")}>
                {isMonday && (
                  <div key={`w-${format(day, "yyyy-MM-dd")}`} className="border-b border-r border-white/[0.06]/60 flex items-start justify-center pt-2">
                    <span className="text-[10px] font-mono text-white/25">{getISOWeek(day)}</span>
                  </div>
                )}
                <div
                  key={format(day, "yyyy-MM-dd")}
                  className={cn(
                    "min-h-[90px] sm:min-h-[110px] p-1.5 border-b border-r border-white/[0.06]/60 transition-colors relative group",
                    !isCurrentMonth && "bg-[#0d0d0d]/60",
                    isWeekend && isCurrentMonth && "bg-red-500/[0.03]",
                    isWeekend && !isCurrentMonth && "bg-red-500/[0.02]",
                    isToday && "bg-[#6dbf4a]/10 ring-2 ring-inset ring-[#6dbf4a]/40",
                    !isWeekend && isCurrentMonth && "hover:bg-white/[0.02]"
                  )}
                >
                {/* Day number + add button */}
                <div className="flex items-center justify-between mb-1">
                  <span className={cn(
                    "text-sm font-medium leading-none",
                    isToday && "bg-[#6dbf4a] text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                    !isToday && !isCurrentMonth && "text-white/25",
                    !isToday && isCurrentMonth && isWeekend && "text-red-400/70",
                    !isToday && isCurrentMonth && !isWeekend && "text-gray-200"
                  )}>
                    {format(day, "d")}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); openAddDialog(day); }}
                    className="w-5 h-5 rounded flex items-center justify-center text-white/40 hover:text-[#6dbf4a] hover:bg-[#6dbf4a]/20 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Lägg till"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Schedules for this day */}
                <div className="space-y-0.5">
                  {daySchedules.slice(0, hasMany ? 2 : 3).map(schedule => {
                    const typeInfo = getTypeInfo(schedule.schedule_type);
                    const displayTitle = schedule.title
                      || (schedule.schedule_type === "projekt" ? schedule.project_name : typeInfo.label)
                      || typeInfo.label;

                    return (
                      <div
                        key={schedule.id}
                        onClick={(e) => { e.stopPropagation(); openEditDialog(schedule); }}
                        className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] sm:text-xs cursor-pointer hover:brightness-110 transition-all"
                        style={{ backgroundColor: `${schedule.color || typeInfo.color}20`, borderLeft: `3px solid ${schedule.color || typeInfo.color}` }}
                        title={`${schedule.user_name}: ${displayTitle}${schedule.notes ? '\n' + schedule.notes : ''}`}
                      >
                        <span className="truncate text-white font-medium">
                          <span className="hidden sm:inline text-white/40" style={{ color: `${schedule.color || typeInfo.color}` }}>
                            {schedule.user_name?.split(' ')[0]}
                          </span>
                          <span className="hidden sm:inline text-white/40 mx-0.5">&middot;</span>
                          {displayTitle.length > 18 ? displayTitle.substring(0, 18) + '...' : displayTitle}
                        </span>
                      </div>
                    );
                  })}
                  {hasMany && (
                    <button
                      onClick={(e) => openDayDetail(day, e)}
                      className="text-[10px] text-[#6dbf4a] hover:text-[#5da83d] px-1.5 font-medium cursor-pointer"
                      data-testid={`show-more-${format(day, "yyyy-MM-dd")}`}
                    >
                      +{daySchedules.length - 2} till...
                    </button>
                  )}
                </div>

                {/* Click to view day details if there are entries */}
                {daySchedules.length > 0 && (
                  <button
                    onClick={(e) => openDayDetail(day, e)}
                    className="absolute bottom-0.5 right-0.5 w-4 h-4 rounded-full bg-[#6dbf4a]/20 text-[#6dbf4a] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-[9px] font-bold"
                    title="Visa alla"
                  >
                    {daySchedules.length}
                  </button>
                )}
              </div>
              </Fragment>
            );
          })}
        </div>
      </Card>

      {/* Day Detail Modal */}
      <Dialog open={dayDetailOpen} onOpenChange={setDayDetailOpen}>
        <DialogContent className="bg-[#0d1218] border-white/[0.06] max-w-lg" data-testid="day-detail-modal">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-white flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-[#6dbf4a]" />
                {dayDetailDate && format(dayDetailDate, "EEEE d MMMM yyyy", { locale: sv })}
              </DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {dayDetailSchedules.length > 0 ? (
              dayDetailSchedules.map(schedule => {
                const typeInfo = getTypeInfo(schedule.schedule_type);
                const displayTitle = schedule.title
                  || (schedule.schedule_type === "projekt" ? schedule.project_name : typeInfo.label);
                const canEdit = isAdmin || schedule.user_id === user?.id;

                return (
                  <div
                    key={schedule.id}
                    className="bg-[#080c12] rounded-xl p-4 border-l-4 hover:bg-[#0a0e14] transition-colors"
                    style={{ borderLeftColor: schedule.color || typeInfo.color }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs" style={{
                            borderColor: `${schedule.color || typeInfo.color}60`,
                            color: schedule.color || typeInfo.color
                          }}>
                            {typeInfo.label}
                          </Badge>
                          <span className="text-sm text-white/40">{schedule.user_name}</span>
                        </div>
                        <p className="text-white font-medium">{displayTitle}</p>
                        {schedule.notes && (
                          <p className="text-sm text-white/40 mt-1 flex items-start gap-1.5">
                            <FileText className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-white/40" />
                            {schedule.notes}
                          </p>
                        )}
                        {schedule.start_date !== schedule.end_date && (
                          <p className="text-xs text-white/40 mt-1">
                            {schedule.start_date} → {schedule.end_date}
                          </p>
                        )}
                      </div>
                      {canEdit && (
                        <div className="flex gap-1 flex-shrink-0">
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-white/40 hover:text-white"
                            onClick={() => { setDayDetailOpen(false); openEditDialog(schedule); }}>
                            <FileText className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-white/40 hover:text-red-400"
                            onClick={() => handleDelete(schedule.id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8 text-white/40">
                <CalendarDays className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">Inga bokningar denna dag</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button size="sm" onClick={() => { setDayDetailOpen(false); openAddDialog(dayDetailDate); }}
              className="bg-[#6dbf4a] hover:bg-[#5da83d] gap-1" data-testid="add-from-day-detail">
              <Plus className="w-4 h-4" /> Lägg till
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0d1218] border-white/[0.06] max-w-[95vw] sm:max-w-md" data-testid="schedule-form-modal">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingSchedule ? "Redigera" : "Lägg till"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            {isAdmin ? (
              <div className="space-y-1">
                <Label className="text-white/40 text-sm">
                  Anställda {!editingSchedule && form.user_ids.length > 0 && <span className="text-[#6dbf4a]">({form.user_ids.length} valda)</span>}
                </Label>
                {editingSchedule ? (
                  /* Single select when editing */
                  <Select value={form.user_ids[0] || ""} onValueChange={(v) => setForm({ ...form, user_ids: [v], user_id: v })}>
                    <SelectTrigger className="bg-[#080c12] border-white/[0.06] text-white" data-testid="schedule-user">
                      <SelectValue placeholder="Välj" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0d1218] border-white/[0.06]">
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id} className="text-white">{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  /* Multi-select checkboxes when creating */
                  <div className="bg-[#080c12] border border-white/[0.06] rounded-md p-2 space-y-1 max-h-[150px] overflow-y-auto">
                    {/* Select all */}
                    <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#333] cursor-pointer border-b border-white/[0.06] mb-1 pb-2">
                      <input type="checkbox"
                        checked={form.user_ids.length === users.length && users.length > 0}
                        onChange={(e) => setForm({ ...form, user_ids: e.target.checked ? users.map(u => u.id) : [] })}
                        className="rounded border-[#555] bg-[#080c12] text-[#6dbf4a] focus:ring-[#6dbf4a]" />
                      <span className="text-sm text-white/40 font-medium">Alla</span>
                    </label>
                    {users.map(u => (
                      <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#333] cursor-pointer">
                        <input type="checkbox"
                          checked={form.user_ids.includes(u.id)}
                          onChange={(e) => {
                            const ids = e.target.checked
                              ? [...form.user_ids, u.id]
                              : form.user_ids.filter(id => id !== u.id);
                            setForm({ ...form, user_ids: ids });
                          }}
                          className="rounded border-[#555] bg-[#080c12] text-[#6dbf4a] focus:ring-[#6dbf4a]" />
                        <span className="text-sm text-white">{u.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="p-2 bg-[#080c12] rounded border border-white/[0.06]">
                <span className="text-white/40 text-sm">Anställd: </span>
                <span className="text-white text-sm font-medium">{user?.name}</span>
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-white/40 text-sm">Typ</Label>
              <Select value={form.schedule_type} onValueChange={(v) => setForm({ ...form, schedule_type: v, custom_type: "" })}>
                <SelectTrigger className="bg-[#080c12] border-white/[0.06] text-white" data-testid="schedule-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0d1218] border-white/[0.06] max-h-[200px]">
                  {SCHEDULE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value} className="text-white">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded" style={{ backgroundColor: t.color }} />
                        {t.label}
                      </div>
                    </SelectItem>
                  ))}
                  <SelectItem value="annat" className="text-white">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded bg-gray-400" />
                      Skriv eget...
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              {form.schedule_type === "annat" && (
                <Input value={form.custom_type}
                  onChange={(e) => setForm({ ...form, custom_type: e.target.value })}
                  placeholder="Skriv typ, t.ex. Besiktning, Leverans..."
                  className="mt-1 bg-[#080c12] border-[#6dbf4a] text-white"
                  autoFocus data-testid="schedule-custom-type" />
              )}
            </div>

            {form.schedule_type === "projekt" && projects.length > 0 && (
              <div className="space-y-1">
                <Label className="text-white/40 text-sm">Projekt</Label>
                <Select value={form.project_id || ""} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                  <SelectTrigger className="bg-[#080c12] border-white/[0.06] text-white" data-testid="schedule-project">
                    <SelectValue placeholder="Välj projekt" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0d1218] border-white/[0.06]">
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id} className="text-white">{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-white/40 text-sm">Vad ska du göra?</Label>
              <Input value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="T.ex. 'Måla fasad', 'Kundmöte Stockholm'"
                className="bg-[#080c12] border-white/[0.06] text-white" data-testid="schedule-title" />
            </div>

            <div className="space-y-1">
              <Label className="text-white/40 text-sm">Anteckningar (valfritt)</Label>
              <textarea value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Skriv detaljer här..."
                rows={2}
                className="flex w-full rounded-md border bg-[#080c12] border-white/[0.06] text-white px-3 py-2 text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#6dbf4a] focus:border-transparent"
                data-testid="schedule-notes" />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-white/40 text-sm">Från</Label>
                <Input type="date" value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  className="bg-[#080c12] border-white/[0.06] text-white" data-testid="schedule-start-date" />
              </div>
              <div className="space-y-1">
                <Label className="text-white/40 text-sm">Till</Label>
                <Input type="date" value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  className="bg-[#080c12] border-white/[0.06] text-white" data-testid="schedule-end-date" />
              </div>
            </div>

            <DialogFooter className="gap-2 pt-2">
              {editingSchedule && (
                <Button type="button" variant="destructive" size="sm"
                  onClick={() => handleDelete(editingSchedule.id)} className="mr-auto" data-testid="schedule-delete-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
              <Button type="button" variant="outline" size="sm" onClick={() => setDialogOpen(false)} className="border-white/[0.06] text-white/40">
                Avbryt
              </Button>
              <Button type="submit" size="sm" disabled={submitting} className="bg-[#6dbf4a] hover:bg-[#5da83d]" data-testid="schedule-save-btn">
                {submitting ? "..." : "Spara"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Schedule;
